<template>
  <div :id="modalId" class="modal overflow-y-auto flex" tabindex="-1" aria-hidden="true" :class="{ show: isOpen }">
    <div class="modal-dialog">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header px-5 py-3 border-b border-gray-200 dark:border-dark-5">
          <h2 class="font-medium text-base">
            {{ $t(`${viewRoot}.new`) }}
          </h2>
        </div>

        <!-- Modal Body -->
        <div class="modal-body px-5 py-6">
          <!-- Dynamic Tabs -->
          <div class="lang__tabs nav nav-tabs intro-y flex-col sm:flex-row text-gray-600 rounded-b-none" role="tablist">
            <a
              v-for="lang in langs"
              :key="lang.key"
              @click="changeLang(lang.key)"
              href="javascript:;"
              class="tooltip w-full sm:w-40 py-4 text-center flex justify-center items-center rounded-t-md"
              :class="activeLangKey === lang.key ? 'active border-transparent' : ''"
            >
              {{ $t("components.langs." + lang.key + ".name") }}
            </a>
          </div>

          <!-- Language Inputs -->
          <div class="mt-5">
            <div v-for="lang in langs" :key="lang.key" v-show="activeLangKey === lang.key">
              <label :for="'quick-benefit-name-' + lang.key" class="form-label font-semibold">
                {{ $t(`${viewRoot}.form.name.label`) }}
              </label>
              <div class="input-group">
                <textarea
                  :id="'quick-benefit-name-' + lang.key"
                  rows="3"
                  class="form-control w-full bg-theme-32"
                  :class="validationError && !benefit.langs[lang.key].name.trim() ? 'border border-theme-6' : ''"
                  :placeholder="$t(`${viewRoot}.form.name.placeholder`)"
                  v-model="benefit.langs[lang.key].name"
                  @input="clearValidationError"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- Error messages -->
          <div v-if="validationError" class="text-theme-6 mt-3 text-sm font-semibold">
            {{ validationError }}
          </div>
          <div v-if="apiError" class="text-theme-6 mt-3 text-sm font-semibold">
            {{ apiError }}
          </div>
        </div>

        <!-- Modal Footer -->
        <div
          class="modal-footer px-5 py-3 border-t border-gray-200 dark:border-dark-5 text-right flex justify-end gap-3"
        >
          <button type="button" @click="close" class="btn btn-outline-secondary w-24 mr-2" :disabled="loading">
            {{ $t("components.cancel") }}
          </button>
          <button
            type="button"
            @click="save"
            class="btn btn-primary w-24 flex items-center justify-center"
            :disabled="loading"
          >
            <LoadingIcon v-if="loading" icon="three-dots" class="w-4 h-4 ml-2" />
            <span v-else>{{ $t("views.save") }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import NutrihubService from "@/services/nutrihub.service";
import Toastify from "toastify-js";
import { ensureShape, validateBenefit, formatToSend } from "@/utils/modules/nutrihub/supplements/benefits.js";

export default defineComponent({
  name: "BenefitQuickCreateModal",
  props: {
    modalId: {
      type: String,
      default: "benefit-quick-create-modal",
    },
  },
  emits: ["created"],
  data() {
    return {
      viewRoot: "views.supplements_benefits",
      isOpen: false,
      loading: false,
      langs: this.$global.getLangs(),
      activeLangKey: "es",
      benefit: {
        id: null,
        langs: {},
        validations: {
          name: "",
        },
      },
      validationError: "",
      apiError: "",
      targetIndex: null,
    };
  },
  created() {
    this.resetForm();
  },
  methods: {
    resetForm() {
      this.activeLangKey = this.langs[0]?.key || "es";
      this.benefit = ensureShape({ id: null }, this.langs);
      this.validationError = "";
      this.apiError = "";
    },
    show(index) {
      this.targetIndex = index;
      this.resetForm();
      this.isOpen = true;
      cash("#" + this.modalId).modal("show");
    },
    close() {
      this.isOpen = false;
      cash("#" + this.modalId).modal("hide");
    },
    changeLang(key) {
      this.activeLangKey = key;
    },
    clearValidationError() {
      this.validationError = "";
    },
    async save() {
      this.validationError = "";
      this.apiError = "";

      const isValid = validateBenefit(this.benefit, this.langs, { t: this.$t });

      if (!isValid) {
        this.validationError = this.benefit.validations.name;
        return;
      }

      this.loading = true;

      const [payload] = formatToSend(this.benefit);

      try {
        const { data } = await NutrihubService.createSupplementBenefit(payload);

        // Emit event to parent with the newly created benefit details and trigger index
        this.$emit("created", {
          benefit: data,
          index: this.targetIndex,
        });

        this.showToast();
        this.close();
      } catch (error) {
        console.error("Error creating benefit via quick modal:", error);
        this.apiError = error?.response?.data?.message || error?.message || "Error al guardar el beneficio.";
      } finally {
        this.loading = false;
      }
    },
    showToast() {
      const id = "saved-confirmation-success";
      const node = cash(`#${id}`).clone().removeClass("hidden")[0];
      if (node) {
        Toastify({
          node: node,
          duration: 3000,
          gravity: "top",
          position: "right",
          close: true,
        }).showToast();
      } else {
        const customNode = cash(`
          <div class="toastify-content flex">
            <div class="text-theme-9">✓</div>
            <div class="ml-4 mr-4">
              <div class="font-medium">${this.$t("components.message_modal.saved") || "Guardado con éxito."}</div>
            </div>
          </div>
        `)[0];
        Toastify({
          node: customNode,
          duration: 3000,
          gravity: "top",
          position: "right",
          close: true,
        }).showToast();
      }
    },
  },
});
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
